import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../../auth/AuthProvider'
import { ConfirmDangerModal } from '../ConfirmDangerModal'
import { IconButton } from '../IconButton'
import { Modal } from '../Modal'
import { SortableTh } from '../SortableTh'
import { useTableSort } from '../../hooks/useTableSort'
import {
  TURNOS_ESCOLA_UI,
  emptyTurnosEscolaForm,
  totalNivelRecord,
  turnoEscolaHabilitado,
  turnosFormFromRecord,
  turnosFormToRecordPayload,
  type TurnoEscolaFieldKey,
  type TurnosEscolaForm,
} from '../../lib/turnosEscola'
import { supabase } from '../../lib/supabase'
import type { Escola, EscolaCeact } from '../../types/database'
import { NIVEIS_R065 } from '../../types/database'

const PAGE_SIZE = 30
const STORAGE_PAGE = 'escolas-ceact-page'
const STORAGE_SORT = 'escolas-ceact-sort'

type EscolaPicker = Pick<Escola, 'id' | 'nome' | 'codigo_semed'>

type EscolaCeactRow = EscolaCeact & {
  escola: EscolaPicker
}

interface EscolasCeactSectionProps {
  embedded?: boolean
}

function readPage() {
  const n = Number(sessionStorage.getItem(STORAGE_PAGE) || '1')
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1
}

function normalizeForSearch(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
}

function escolaMatchesFilter(escola: EscolaPicker, query: string) {
  const q = query.trim()
  if (!q) return true
  const tokens = q.split(/\s+/).filter(Boolean).map(normalizeForSearch)
  const nome = normalizeForSearch(escola.nome)
  const id = String(escola.codigo_semed ?? '')
  return tokens.every((token) => nome.includes(token) || id.includes(token))
}

export function EscolasCeactSection({ embedded = false }: EscolasCeactSectionProps) {
  const { canWrite, canDelete } = useAuth()
  const [rows, setRows] = useState<EscolaCeactRow[]>([])
  const [escolasBase, setEscolasBase] = useState<EscolaPicker[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<EscolaCeactRow | null>(null)
  const [nomeBusca, setNomeBusca] = useState('')
  const [escolaSelecionada, setEscolaSelecionada] = useState<EscolaPicker | null>(null)
  const [turnosForm, setTurnosForm] = useState<TurnosEscolaForm>(emptyTurnosEscolaForm())
  const [deleteTarget, setDeleteTarget] = useState<EscolaCeactRow | null>(null)
  const [page, setPage] = useState(readPage)
  const hasLoadedRef = useRef(false)
  const writable = canWrite('escolas')
  const tableSort = useTableSort<EscolaCeactRow>('codigo', 'asc', STORAGE_SORT)

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    setError(null)
    const [ceact, escolas] = await Promise.all([
      supabase.from('escolas_ceact').select('*, escola:escolas(id, nome, codigo_semed)'),
      supabase
        .from('escolas')
        .select('id, nome, codigo_semed')
        .not('codigo_semed', 'is', null)
        .order('nome'),
    ])
    if (ceact.error || escolas.error) {
      setError(ceact.error?.message || escolas.error?.message || 'Erro ao carregar escolas CEACT')
    } else {
      const loaded = ((ceact.data as EscolaCeactRow[]) || []).filter((r) => r.escola?.codigo_semed != null)
      setRows(loaded)
      setEscolasBase((escolas.data as EscolaPicker[]) || [])
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    if (!embedded) document.title = 'Escolas CEACT · ISEN EDUCAE'
  }, [embedded])

  useEffect(() => {
    if (hasLoadedRef.current) return
    hasLoadedRef.current = true
    load()
  }, [load])

  useEffect(() => {
    sessionStorage.setItem(STORAGE_PAGE, String(page))
  }, [page])

  const ceactEscolaIds = useMemo(() => new Set(rows.map((r) => r.escola_id)), [rows])

  const escolasDisponiveis = useMemo(() => {
    const excludeId = editing?.escola_id
    return escolasBase.filter((e) => e.codigo_semed != null && (!ceactEscolaIds.has(e.id) || e.id === excludeId))
  }, [escolasBase, ceactEscolaIds, editing])

  const escolasFiltradas = useMemo(() => {
    return escolasDisponiveis.filter((e) => escolaMatchesFilter(e, nomeBusca))
  }, [escolasDisponiveis, nomeBusca])

  const filteredSorted = useMemo(() => {
    const accessors: Record<string, (r: EscolaCeactRow) => string | number> = {
      nome: (r) => r.escola.nome,
      codigo: (r) => r.escola.codigo_semed ?? 0,
      infantil: (r) => totalNivelRecord(r, 'infantil'),
      fundMenor: (r) => totalNivelRecord(r, 'fund_menor'),
      fundMaior: (r) => totalNivelRecord(r, 'fund_maior'),
      eja: (r) => totalNivelRecord(r, 'eja'),
      total: (r) => r.total,
    }
    return tableSort.sortRows(rows, accessors)
  }, [rows, tableSort])

  const totalPages = Math.max(1, Math.ceil(filteredSorted.length / PAGE_SIZE))

  useEffect(() => {
    // Antes da carga a lista está vazia (totalPages = 1) e o clamp descartaria a página persistida.
    if (loading) return
    setPage((p) => Math.min(p, totalPages))
  }, [loading, totalPages])

  const pageRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE
    return filteredSorted.slice(start, start + PAGE_SIZE)
  }, [filteredSorted, page])

  function resetModal() {
    setModalOpen(false)
    setEditing(null)
    setNomeBusca('')
    setEscolaSelecionada(null)
    setTurnosForm(emptyTurnosEscolaForm())
  }

  function openNew() {
    setError(null)
    setEditing(null)
    setNomeBusca('')
    setEscolaSelecionada(null)
    setTurnosForm(emptyTurnosEscolaForm())
    setModalOpen(true)
  }

  function openEdit(row: EscolaCeactRow) {
    setError(null)
    setEditing(row)
    setNomeBusca(row.escola.nome)
    setEscolaSelecionada(row.escola)
    setTurnosForm(turnosFormFromRecord(row))
    setModalOpen(true)
  }

  async function save() {
    const escola = editing ? editing.escola : escolaSelecionada
    if (!escola) {
      setError('Selecione uma escola da lista')
      return
    }
    const payload = {
      escola_id: escola.id,
      ...turnosFormToRecordPayload(turnosForm),
      atualizado_em: new Date().toISOString(),
    }
    const q = editing
      ? supabase.from('escolas_ceact').update(payload).eq('id', editing.id)
      : supabase.from('escolas_ceact').insert(payload)
    const { error: err } = await q
    if (err) {
      setError(err.message)
      return
    }
    resetModal()
    setError(null)
    load(true)
  }

  function selectEscola(escola: EscolaPicker) {
    setEscolaSelecionada(escola)
  }

  if (loading && !rows.length && !embedded) return <div className="loading">Carregando escolas CEACT…</div>

  const pageStart = filteredSorted.length ? (page - 1) * PAGE_SIZE + 1 : 0
  const pageEnd = Math.min(page * PAGE_SIZE, filteredSorted.length)

  return (
    <div className={`escolas-section${embedded ? ' escolas-section-embedded' : ''}`}>
      <div
        className={embedded ? undefined : 'page-header'}
        style={embedded ? { display: 'flex', justifyContent: 'flex-end', marginBottom: 12 } : undefined}
      >
        {!embedded && <h1>Escolas CEACT</h1>}
        <div className="page-actions">
          {writable && (
            <button type="button" className="btn btn-primary" onClick={openNew}>
              Nova Escola CEACT
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="kpi-sub" style={{ marginBottom: 12 }}>
        {filteredSorted.length} escolas CEACT
      </div>

      <div className="escolas-table-shell">
        <div className="escolas-table-scroll">
          <table className="data-table data-table-escolas">
            <thead className="escolas-thead-sticky">
              <tr>
                <SortableTh
                  label="ID"
                  sortKey="codigo"
                  activeKey={tableSort.sortKey}
                  direction={tableSort.sortDir}
                  onSort={tableSort.toggleSort}
                  col="num"
                />
                <SortableTh
                  label="Nome escola"
                  sortKey="nome"
                  activeKey={tableSort.sortKey}
                  direction={tableSort.sortDir}
                  onSort={tableSort.toggleSort}
                  col="name"
                />
                <SortableTh
                  label="Infantil"
                  sortKey="infantil"
                  activeKey={tableSort.sortKey}
                  direction={tableSort.sortDir}
                  onSort={tableSort.toggleSort}
                  col="num"
                />
                <SortableTh
                  label="Fund. Menor"
                  sortKey="fundMenor"
                  activeKey={tableSort.sortKey}
                  direction={tableSort.sortDir}
                  onSort={tableSort.toggleSort}
                  col="num"
                />
                <SortableTh
                  label="Fund. Maior"
                  sortKey="fundMaior"
                  activeKey={tableSort.sortKey}
                  direction={tableSort.sortDir}
                  onSort={tableSort.toggleSort}
                  col="num"
                />
                <SortableTh
                  label="EJA"
                  sortKey="eja"
                  activeKey={tableSort.sortKey}
                  direction={tableSort.sortDir}
                  onSort={tableSort.toggleSort}
                  col="num"
                />
                <SortableTh
                  label="Total"
                  sortKey="total"
                  activeKey={tableSort.sortKey}
                  direction={tableSort.sortDir}
                  onSort={tableSort.toggleSort}
                  col="num"
                />
                {writable && <th className="col-actions">Ações</th>}
              </tr>
            </thead>
            <tbody>
              {loading && !rows.length ? (
                <tr>
                  <td colSpan={writable ? 8 : 7} className="kpi-sub" style={{ textAlign: 'center', padding: 24 }}>
                    Carregando escolas CEACT…
                  </td>
                </tr>
              ) : !pageRows.length ? (
                <tr>
                  <td colSpan={writable ? 8 : 7} className="kpi-sub" style={{ textAlign: 'center', padding: 24 }}>
                    Nenhuma escola CEACT cadastrada.
                  </td>
                </tr>
              ) : (
                pageRows.map((r) => (
                  <tr key={r.id}>
                    <td className="col-num">{r.escola.codigo_semed}</td>
                    <td className="col-name">{r.escola.nome}</td>
                    <td className="col-num">{totalNivelRecord(r, 'infantil') || ''}</td>
                    <td className="col-num">{totalNivelRecord(r, 'fund_menor') || ''}</td>
                    <td className="col-num">{totalNivelRecord(r, 'fund_maior') || ''}</td>
                    <td className="col-num">{totalNivelRecord(r, 'eja') || ''}</td>
                    <td className="col-num">
                      <strong>{r.total}</strong>
                    </td>
                    {writable && (
                      <td className="col-actions">
                        <div className="table-actions">
                          <IconButton variant="edit" label={`Editar ${r.escola.nome}`} onClick={() => openEdit(r)} />
                          {canDelete('escolas') && (
                            <IconButton
                              variant="delete"
                              label={`Excluir ${r.escola.nome} da lista CEACT`}
                              onClick={() => setDeleteTarget(r)}
                            />
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="escolas-table-footer">
          <div className="escolas-pagination-info kpi-sub">
            {filteredSorted.length
              ? `Exibindo ${pageStart}–${pageEnd} de ${filteredSorted.length} escolas`
              : 'Nenhuma escola CEACT'}
          </div>
          <div className="escolas-pagination">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Anterior
            </button>
            <span className="escolas-pagination-label">
              Página {page} de {totalPages}
            </span>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Próxima
            </button>
          </div>
        </div>
      </div>

      <Modal
        open={modalOpen}
        title={editing ? 'Editar escola CEACT' : 'Nova escola CEACT'}
        size="lg"
        onClose={resetModal}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={resetModal}>
              Cancelar
            </button>
            <button type="button" className="btn btn-primary" onClick={save}>
              Salvar
            </button>
          </>
        }
      >
        {error && <div className="alert alert-error">{error}</div>}
        <p className="kpi-sub" style={{ marginBottom: 12 }}>
          Associe uma escola regular e informe os alunos CEACT por modalidade e turno.
        </p>
        <div className="form-grid">
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label>Nome da escola *</label>
            {editing ? (
              <input value={`${editing.escola.codigo_semed} — ${editing.escola.nome}`} readOnly />
            ) : (
              <>
                <input
                  value={nomeBusca}
                  onChange={(ev) => {
                    const next = ev.target.value
                    setNomeBusca(next)
                    if (escolaSelecionada && !escolaMatchesFilter(escolaSelecionada, next)) {
                      setEscolaSelecionada(null)
                    }
                  }}
                  placeholder="Filtrar por nome ou ID (busca parcial)"
                  autoComplete="off"
                />
                <ul className="escola-ceact-sugestoes" role="listbox" aria-label="Escolas disponíveis">
                  {escolasFiltradas.map((e) => (
                    <li key={e.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={escolaSelecionada?.id === e.id}
                        className={`escola-ceact-sugestao-btn${escolaSelecionada?.id === e.id ? ' selected' : ''}`}
                        onClick={() => selectEscola(e)}
                      >
                        <span className="col-num">{e.codigo_semed}</span> — {e.nome}
                      </button>
                    </li>
                  ))}
                </ul>
                {!escolasFiltradas.length && (
                  <p className="kpi-sub" style={{ marginTop: 8 }}>
                    {escolasDisponiveis.length
                      ? 'Nenhuma escola corresponde ao filtro.'
                      : 'Todas as escolas já estão na lista CEACT.'}
                  </p>
                )}
                {escolaSelecionada && (
                  <p className="kpi-sub" style={{ marginTop: 8 }}>
                    Selecionada: <strong>{escolaSelecionada.codigo_semed} — {escolaSelecionada.nome}</strong>
                  </p>
                )}
              </>
            )}
          </div>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label>Matrículas CEACT por modalidade e turno</label>
            <p className="kpi-sub" style={{ margin: '0 0 8px' }}>
              Informe a quantidade de alunos CEACT em cada turno. Infantil não possui turno noturno.
            </p>
            <div className="escola-turnos-grid">
              <table className="escola-turnos-table">
                <colgroup>
                  <col className="escola-turnos-col-modalidade" />
                  <col span={4} className="escola-turnos-col-turno" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Modalidade</th>
                    {TURNOS_ESCOLA_UI.map((t) => (
                      <th key={t.key} title={t.label}>
                        {t.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {NIVEIS_R065.map((nivel) => (
                    <tr key={nivel.key}>
                      <th scope="row">{nivel.label}</th>
                      {TURNOS_ESCOLA_UI.map((turno) => {
                        const field = `${nivel.key}_${turno.key}` as TurnoEscolaFieldKey
                        const habilitado = turnoEscolaHabilitado(nivel.key, turno.key)
                        return (
                          <td key={turno.key}>
                            <input
                              type="number"
                              min={0}
                              step={1}
                              disabled={!habilitado}
                              value={habilitado ? turnosForm[field] || '' : ''}
                              onChange={(ev) => {
                                const raw = ev.target.value
                                setTurnosForm((prev) => ({
                                  ...prev,
                                  [field]: raw === '' ? 0 : Math.max(0, Math.floor(Number(raw) || 0)),
                                }))
                              }}
                            />
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmDangerModal
        open={!!deleteTarget}
        title="Remover escola CEACT"
        actionName="REMOVER CEACT"
        description={`Isso remove "${deleteTarget?.escola.nome}" da lista CEACT. A escola permanece cadastrada em Escolas.`}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          if (!deleteTarget) return
          const { error: err } = await supabase.from('escolas_ceact').delete().eq('id', deleteTarget.id)
          if (err) throw new Error(err.message || 'Falha ao remover escola CEACT')
          load(true)
        }}
      />
    </div>
  )
}
